import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { fetchWithAuth } from '../../libs/apiUtils'
import Modal from '../Modal'

interface EditTableRequest {
  id?: string
  name?: string
  description?: string
  categoryId?: string
  adminId?: string
  isPublic?: boolean
}

interface EditTableModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  table: EditTableRequest
  onSuccess: () => void
}

export default function EditTableModal({
  isOpen,
  setIsOpen,
  table,
  onSuccess,
}: EditTableModalProps) {
  const [formData, setFormData] = useState<EditTableRequest>({
    ...table,
    name: '',
    description: '',
    adminId: '',
    isPublic: false
  })
  const [loading, setLoading] = useState(false)
  const [adminOptions, setAdminOptions] = useState<{id: string, value: string}[]>([])
  const userRoleSlug = localStorage.getItem('user_role_slug') || ''

  useEffect(() => {
    if (userRoleSlug === 'super_admin' && isOpen) {
      loadAdmins()
    }
  }, [userRoleSlug, isOpen])

  async function loadAdmins() {
    try {
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/admins`, {
        method: 'GET'
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setAdminOptions(data.data || [])
      }
    } catch (err) {
      console.error('Error loading admins', err)
    }
  }

  useEffect(() => {
    if (table) {
      setFormData({
        name: table.name || '',
        description: table.description || '',
        id: table.id,
        categoryId: table.categoryId,
        adminId: table.adminId || '',
        isPublic: table.isPublic || false
      })
      
      console.log(table)
    }
  }, [table])

  // Updated to support checkboxes properly
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target
    const name = target.name
    const value = target.type === 'checkbox' ? (target as HTMLInputElement).checked : target.value

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formData.name?.trim()) return toast.error('Nama tabel tidak boleh kosong')
    if (!formData.id) return toast.error('Tidak ada tabel yang dipilih')
    if (userRoleSlug === 'super_admin' && !formData.adminId) return toast.error('Silakan pilih admin')

    try {
      setLoading(true)
      const res = await fetchWithAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/table`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      )

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0]?.message || 'Gagal memperbarui tabel')
        return
      }

      toast.success('Tabel berhasil diperbarui')
      setIsOpen(false)
      onSuccess()
    } catch (err) {
      toast.error('Kesalahan jaringan, periksa koneksi Anda')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h3 className="text-xl font-bold text-neutral-800 mb-4">Edit Tabel</h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nama Tabel
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={formData.name}
            onChange={handleChange}
            placeholder="Contoh: Daftar Jalan Nasional"
            required
            autoFocus
          />
        </div>

        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-semibold text-neutral-600 mb-2">
            Deskripsi (Opsional)
          </label>
          <textarea
            id="description"
            name="description"
            rows={3}
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={formData.description}
            onChange={handleChange}
            placeholder="Keterangan mengenai tabel ini..."
          />
        </div>

        {userRoleSlug === 'super_admin' && (
          <div className="mb-4">
            <label htmlFor="adminId" className="block text-sm font-semibold text-neutral-600 mb-2">
              Admin
            </label>
            <select
              id="adminId"
              name="adminId"
              className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none bg-white"
              value={formData.adminId}
              onChange={handleChange}
              required
            >
              <option value="">Pilih Admin</option>
              {adminOptions.map(admin => (
                <option key={admin.id} value={admin.id}>{admin.value}</option>
              ))}
            </select>
          </div>
        )}

        {/* Standardized Toggle Form Field */}
        <div className="mb-6">
          <label htmlFor="isPublic" className="block text-sm font-semibold text-neutral-600 mb-2">
            Akses Tabel
          </label>
          <label
            htmlFor="isPublic"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full flex items-center justify-between cursor-pointer focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all bg-white"
          >
            <span className="text-sm text-neutral-700 font-medium">
              {formData.isPublic ? 'Publik (Dapat dilihat tanpa login)' : 'Privat (Hanya pengguna terautentikasi)'}
            </span>
            <div className="relative inline-flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                name="isPublic"
                checked={formData.isPublic}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-neutral-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-neutral-600 font-medium rounded-lg hover:bg-neutral-100 transition-colors"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
